import './BuckChat.css';
import 'react-loading-skeleton/dist/skeleton.css';

import {
  AIResponse,
  Box,
  Button,
  ChatWidget,
  ListingChat,
  MessageBubble,
  Text,
  ThinkingSteps,
} from '@landtrustinc/design-system';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Backpressure from './components/Backpressure/Backpressure';
import BudgetExhausted from './components/BudgetExhausted/BudgetExhausted';
import ChatError from './components/ChatError/ChatError';
import DrawerManager from './components/DrawerManager/DrawerManager';
import EmptyState, { getEmptyStateDefaultContent } from './components/EmptyStates/EmptyState';
import LtPlusCta from './components/LtPlusCta/LtPlusCta';
import MockSelector from './components/MockSelector/MockSelector';
import PackageCards from './components/PackageCards/PackageCards';
import SignupRequired from './components/SignupRequired/SignupRequired';
import Throttled from './components/Throttled/Throttled';
import UpgradePrompt from './components/UpgradePrompt/UpgradePrompt';
import buckChatConfig from './config';
import usePackageChat from './hooks/usePackageChat';
import type { BuckChatProps, BuckChatWelcomeContent, PageType, SuggestedPrompt } from './types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from './utils/analytics';
import { isFeatureFlagEnabled } from './utils/localStorage';

const DEFAULT_WIDGET_TITLE = 'Buck AI Assistant';
const DEFAULT_INITIAL_SUGGESTED_PROMPTS_TITLE = 'Popular Topics';
const DEFAULT_FOLLOWUP_SUGGESTED_PROMPTS_TITLE = 'Other Helpful Topics';

// Suggested prompts to help users get started in contextual surfaces
const DEFAULT_SUGGESTED_PROMPTS: SuggestedPrompt[] = [
  { label: 'Property Rules', value: 'What are the property rules?' },
  { label: 'Cancellation Policy', value: 'What is the cancellation policy?' },
  { label: 'Available Property Amenities', value: 'What property amenities are available?' },
  { label: 'Available Lodging', value: 'What lodging options are available?' },
];
const DEFAULT_PLACEHOLDER_BY_PAGE: Partial<Record<PageType, string>> = {
  home: 'Ask me anything...',
  listing: 'Ask anything about this listing...',
  package: 'Ask anything about this package...',
};

const AI_DISCLAIMER_TOOLTIP_TEXT =
  'Buck summaries are AI-generated from listing and package details. Please verify important information with the landowner before booking.';

const mergeWelcomeContent = (
  base?: BuckChatWelcomeContent,
  override?: BuckChatWelcomeContent
): BuckChatWelcomeContent | undefined => {
  if (!base && !override) {
    return undefined;
  }

  return {
    ...base,
    ...override,
    starterPrompts: override?.starterPrompts ?? base?.starterPrompts,
    widgetEmptyState: override?.widgetEmptyState ?? base?.widgetEmptyState,
  };
};

const BuckChat: React.FC<BuckChatProps> = ({
  config,
  listingId,
  packageId,
  pageType,
  currentUser,
  variant = 'contextual',
  onAnalyticsEvent,
  onAuthSuccess,
  onFetchPackage,
  onLtPlusSuccess,
  inlineEntryTitle = 'Have a question?',
  inlineEntryMobileTitle = 'Have a question?',
  placeholder,
  inlineEntryTags = [],
  showInlineEntry = true,
  autoExpandOnSubmit = true,
  inlineEntryIsMobile = false,
  widgetTitle = DEFAULT_WIDGET_TITLE,
  suggestedPrompts = DEFAULT_SUGGESTED_PROMPTS,
  initialSuggestedPromptsTitle = DEFAULT_INITIAL_SUGGESTED_PROMPTS_TITLE,
  followupSuggestedPromptsTitle = DEFAULT_FOLLOWUP_SUGGESTED_PROMPTS_TITLE,
  widgetEmptyState,
  welcomeContent,
}) => {
  const [isChatWidgetExpanded, setIsChatWidgetExpanded] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  // Check if mock WebSocket messages feature flag is enabled
  const isMockMessagesEnabled = isFeatureFlagEnabled(buckChatConfig.featureFlags.MOCK_MESSAGES);

  const displayTitle = inlineEntryIsMobile ? inlineEntryMobileTitle : inlineEntryTitle;

  const {
    messages: chatMessages,
    initializeWebSocket,
    sendMessage,
    updateMessage,
    injectMockMessage,
    isLoading: isChatLoading,
    isInputDisabled,
    retryLastMessage,
    cancelQueuedRequest,
    sendFeedback,
    currentTraceMessage,
    thinkingSteps,
    activeResponseDirective,
    queueStatus,
  } = usePackageChat({
    listingId,
    packageId,
    pageType,
    currentUser,
    config,
  });

  const isInitialHomepageState = variant === 'homepage' && chatMessages.length === 0;
  const defaultWelcomeContent = useMemo(
    () => getEmptyStateDefaultContent({ pageType, variant }),
    [pageType, variant]
  );

  const resolvedWelcomeContent = useMemo(
    () => mergeWelcomeContent(defaultWelcomeContent, welcomeContent),
    [defaultWelcomeContent, welcomeContent]
  );
  const resolvedPlaceholder =
    placeholder ??
    (pageType ? DEFAULT_PLACEHOLDER_BY_PAGE[pageType] : undefined) ??
    'Ask anything...';
  const shouldShowContextualInlineEntry = variant === 'contextual' && showInlineEntry;

  // Feedback handlers
  const handleHelpful = useCallback(
    (messageId: string) => {
      sendFeedback(messageId, true);
    },
    [sendFeedback]
  );

  const handleNotHelpful = useCallback(
    (messageId: string) => {
      sendFeedback(messageId, false);
    },
    [sendFeedback]
  );

  // Determine whether to show suggested prompts
  // Show when: messages are empty OR last message is from assistant (without CTA)
  // Hide when: last message is from user OR currently loading OR input is disabled OR CTA is shown
  const shouldShowSuggestedPrompts = useMemo(() => {
    if (isChatLoading || isInputDisabled) return false;
    if (chatMessages.length === 0) return true;
    const lastMessage = chatMessages[chatMessages.length - 1];
    // Don't show if last message has an LT+ CTA - the CTA replaces helpful topics
    if (lastMessage.ltPlusCta) return false;
    return lastMessage.role === 'assistant';
  }, [chatMessages, isChatLoading, isInputDisabled]);

  const activeSuggestedPrompts = useMemo(() => {
    if (resolvedWelcomeContent?.starterPrompts?.length) {
      return resolvedWelcomeContent.starterPrompts;
    }

    return suggestedPrompts;
  }, [resolvedWelcomeContent?.starterPrompts, suggestedPrompts]);
  const resolvedInlineEntryTags =
    inlineEntryTags.length > 0
      ? inlineEntryTags
      : activeSuggestedPrompts.map((prompt) => prompt.label);

  // Customize the title based on whether it's initial load or not
  const suggestedPromptsTitle = useMemo(() => {
    return chatMessages.length === 0 ? initialSuggestedPromptsTitle : followupSuggestedPromptsTitle;
  }, [chatMessages.length, followupSuggestedPromptsTitle, initialSuggestedPromptsTitle]);

  const resolvedWidgetTitle =
    variant === 'homepage' && isInitialHomepageState
      ? (resolvedWelcomeContent?.widgetTitle ?? '')
      : widgetTitle;

  // Dynamic thinking text based on trace messages and queue status
  const thinkingText = useMemo(() => {
    // If queued, show queue status with the backend message
    if (queueStatus) {
      return queueStatus.message;
    }
    if (currentTraceMessage) {
      return currentTraceMessage;
    }
    return 'Thinking...';
  }, [currentTraceMessage, queueStatus]);

  // Show cancel button when queued
  const showCancelButton = !!queueStatus;

  const handlePromptClick = useCallback(
    (prompt: string) => {
      sendMessage(prompt);
      if (autoExpandOnSubmit) {
        setIsChatWidgetExpanded(true);
      }
    },
    [autoExpandOnSubmit, sendMessage]
  );

  const handleInlineSubmit = useCallback(
    (message: string) => {
      sendMessage(message);
      if (autoExpandOnSubmit) {
        setIsChatWidgetExpanded(true);
      }
    },
    [autoExpandOnSubmit, sendMessage]
  );

  // Track previous message count to detect new messages
  const previousMessageCountRef = useRef(chatMessages.length);

  // Detect new assistant messages while widget is closed
  useEffect(() => {
    const previousCount = previousMessageCountRef.current;
    const currentCount = chatMessages.length;

    // If we have a new message
    if (currentCount > previousCount && !isChatWidgetExpanded) {
      const lastMessage = chatMessages[currentCount - 1];
      // If it's an assistant message (includes both successful responses and errors)
      if (lastMessage && lastMessage.role === 'assistant') {
        setHasUnreadMessages(true);
      }
    }

    // Update the ref for next comparison
    previousMessageCountRef.current = currentCount;
  }, [chatMessages, isChatWidgetExpanded]);

  // Clear notifications when widget is opened
  useEffect(() => {
    if (isChatWidgetExpanded) {
      setHasUnreadMessages(false);
    }
  }, [isChatWidgetExpanded]);

  // Warm the WebSocket connection as soon as chat is opened.
  useEffect(() => {
    if (!isChatWidgetExpanded) {
      return;
    }

    initializeWebSocket().catch(() => undefined);
  }, [isChatWidgetExpanded, initializeWebSocket]);

  const thinkingContent = useMemo(() => {
    if (showCancelButton) {
      return (
        <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
          <Box display="flex" alignItems="center" gap="var(--spacing-2)">
            <Text size="sm" color="var(--text-secondary)">
              {thinkingText}
            </Text>
          </Box>
          <Button size="sm" variant="secondary" onClick={cancelQueuedRequest}>
            Cancel
          </Button>
        </Box>
      );
    }
    // Qualifying turns stay in the same card/spinner language as multi-step
    // loading — single active row, no title or estimated-time footer.
    if (activeResponseDirective === 'ask_qualifying_question') {
      return <ThinkingSteps steps={[{ text: thinkingText ?? 'Thinking...', status: 'active' }]} />;
    }
    if (thinkingSteps.length > 0) {
      return (
        <ThinkingSteps steps={thinkingSteps} estimatedTime="This usually takes ~15 seconds." />
      );
    }
    return undefined;
  }, [activeResponseDirective, showCancelButton, thinkingSteps, thinkingText, cancelQueuedRequest]);

  const resolvedWidgetEmptyState = useMemo<
    React.ComponentProps<typeof ChatWidget>['emptyState']
  >(() => {
    if (isMockMessagesEnabled) {
      return [<MockSelector key="mock-selector" onInjectMessage={injectMockMessage} />];
    }

    if (resolvedWelcomeContent?.widgetEmptyState) {
      return resolvedWelcomeContent.widgetEmptyState as React.ComponentProps<
        typeof ChatWidget
      >['emptyState'];
    }

    if (widgetEmptyState) {
      return widgetEmptyState as React.ComponentProps<typeof ChatWidget>['emptyState'];
    }

    return [
      <EmptyState
        key="preset-empty-state"
        content={resolvedWelcomeContent}
        disabled={isInputDisabled}
        onPromptClick={handlePromptClick}
        pageType={pageType}
        starterPromptsTitle={initialSuggestedPromptsTitle}
        variant={variant}
      />,
    ];
  }, [
    handlePromptClick,
    initialSuggestedPromptsTitle,
    injectMockMessage,
    isInputDisabled,
    isMockMessagesEnabled,
    pageType,
    resolvedWelcomeContent,
    variant,
    widgetEmptyState,
  ]);

  return (
    <div className="buck-chat">
      <DrawerManager
        config={config}
        listingId={listingId}
        packageId={packageId}
        currentUser={currentUser}
        onAnalyticsEvent={onAnalyticsEvent}
        onAuthSuccess={onAuthSuccess}
        onLtPlusSuccess={onLtPlusSuccess}
        updateMessage={updateMessage}
      >
        {({ onSignupClick, onLtPlusCtaClick }) => {
          let hasRenderedAIResponseTitle = false;
          const chatWidgetMessages = chatMessages.map((msg, index) => {
            // Preserve existing behavior for non-standard assistant variants that use their own components
            const previousMsg = index > 0 ? chatMessages[index - 1] : null;
            const isConsecutiveAssistant =
              msg.role === 'assistant' && previousMsg?.role === 'assistant';

            if (msg.role === 'user') {
              const timestampString = msg.timestamp
                ? format(msg.timestamp, 'MMM d, h:mm a')
                : undefined;
              return (
                <MessageBubble key={msg.id} variant="sent" timestamp={timestampString}>
                  {msg.content}
                </MessageBubble>
              );
            }

            // Throttled message with warning styling
            if (msg.isThrottled) {
              return (
                <Throttled
                  key={msg.id}
                  msg={msg}
                  isConsecutiveAssistant={isConsecutiveAssistant}
                  cancelQueuedRequest={cancelQueuedRequest}
                  retryLastMessage={retryLastMessage}
                />
              );
            }

            // Backpressure message (system at capacity) with warning styling
            if (msg.isBackpressure) {
              return (
                <Backpressure
                  key={msg.id}
                  msg={msg}
                  isConsecutiveAssistant={isConsecutiveAssistant}
                  retryLastMessage={retryLastMessage}
                />
              );
            }

            if (msg.hasError) {
              return (
                <ChatError
                  key={msg.id}
                  msg={msg}
                  isConsecutiveAssistant={isConsecutiveAssistant}
                  retryLastMessage={retryLastMessage}
                />
              );
            }
            if (msg.isUpgradePrompt) {
              return <UpgradePrompt key={msg.id} msg={msg} />;
            }
            if (msg.isSignupRequired) {
              return (
                <SignupRequired key={msg.id} msg={msg} onSignupClick={() => onSignupClick(msg)} />
              );
            }
            if (msg.isBudgetExhausted) {
              return <BudgetExhausted key={msg.id} msg={msg} />;
            }

            if (msg.ltPlusCta) {
              return (
                <LtPlusCta
                  key={msg.id}
                  msg={msg}
                  listingId={listingId}
                  packageId={packageId}
                  onAnalyticsEvent={onAnalyticsEvent}
                  onCtaClick={
                    config.stripe ? (ltPlusCta) => onLtPlusCtaClick(ltPlusCta, msg.id) : undefined
                  }
                />
              );
            }

            const showTitle = !hasRenderedAIResponseTitle;
            if (showTitle) {
              hasRenderedAIResponseTitle = true;
            }

            return (
              <React.Fragment key={msg.id}>
                <AIResponse
                  showDisclaimer={msg.showDisclaimer}
                  showHelpfulQuestion={msg.showHelpfulQuestion}
                  markdown={msg.markdown}
                  onHelpfulYes={() => handleHelpful(msg.id)}
                  onHelpfulNo={() => handleNotHelpful(msg.id)}
                  onContactLandownerDisplay={() => {
                    // Fire analytics event when Buck displays a Contact Landowner button
                    onAnalyticsEvent?.(BUCK_EVENTS.CONTACT_LANDOWNER_DISPLAYED, {
                      [ANALYTICS_KEYS.LISTING_ID]: listingId,
                      [ANALYTICS_KEYS.PACKAGE_ID]: packageId || 'N/A',
                      [ANALYTICS_KEYS.MESSAGE_ID]: msg.id,
                    });
                  }}
                  showTitle={showTitle}
                >
                  {msg.content}
                </AIResponse>

                {msg.packageCards && msg.packageCards.length > 0 && (
                  <PackageCards
                    packageCards={msg.packageCards}
                    messageId={msg.id}
                    packageCardsLimit={config.packageCardsLimit}
                    onFetchPackage={onFetchPackage}
                    onAnalyticsEvent={onAnalyticsEvent}
                  />
                )}
              </React.Fragment>
            );
          });

          return (
            <>
              {shouldShowContextualInlineEntry && (
                <Box mb="var(--spacing-5)">
                  <ListingChat
                    onSubmit={handleInlineSubmit}
                    title={displayTitle}
                    placeholder={resolvedPlaceholder}
                    tags={resolvedInlineEntryTags}
                    disabled={isInputDisabled}
                  />
                </Box>
              )}

              <ChatWidget
                title={resolvedWidgetTitle}
                titleTooltipContent={
                  isInitialHomepageState ? undefined : AI_DISCLAIMER_TOOLTIP_TEXT
                }
                messages={chatWidgetMessages}
                onSubmit={sendMessage}
                placeholder={resolvedPlaceholder}
                className={isInitialHomepageState ? 'buck-chat-homepage-widget' : undefined}
                expanded={isChatWidgetExpanded}
                onExpandedChange={setIsChatWidgetExpanded}
                isThinking={isChatLoading}
                thinkingText={thinkingText}
                thinkingContent={thinkingContent}
                suggestedPrompts={
                  shouldShowSuggestedPrompts && !isInitialHomepageState
                    ? activeSuggestedPrompts
                    : undefined
                }
                suggestedPromptsTitle={suggestedPromptsTitle}
                onPromptClick={handlePromptClick}
                closeOnClickOutside={false}
                notificationCount={hasUnreadMessages ? 1 : 0}
                disabled={isInputDisabled}
                containerProps={{
                  mb: [20, 20, 24, 0],
                }}
                emptyState={resolvedWidgetEmptyState}
              />
            </>
          );
        }}
      </DrawerManager>
    </div>
  );
};

export default BuckChat;
